import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Badge, Box, Button, Group, Stack, Text } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { useLocation, useSearchParams } from 'react-router-dom'
import { displayNameUser, TicketsStripBlock, TicketsStripHead } from '@/components/tickets/TicketsListStrips'
import { TicketsListTable, type TicketsListBulkSelectConfig } from '@/components/tickets/TicketsListTable'
import { TicketsListToolbar } from '@/components/tickets/TicketsListToolbar'
import {
  DataTableScroll,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
  PaginationBar,
} from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import { useBookmarks } from '@/lib/bookmarks-context'
import { groupTicketsForDisplay } from '@/lib/group-tickets-for-display'
import { ticketsParamsFromSearch } from '@/lib/tickets-list-api-params'
import {
  TICKETS_PAGE_SIZE_DEFAULT,
  parseTicketsPageSize,
  type TicketsPageSizeOption,
} from '@/lib/tickets-list-page-size'
import { useTicketsListColumnVisibility } from '@/lib/tickets-list-columns'
import {
  useOpenTicketFromList,
  usePersistTicketListScrollPosition,
  useRestoreTicketsListScrollWhenReady,
} from '@/lib/open-ticket-navigation'
import {
  TICKETS_LIST_PAGE_QUERY,
  parseTicketsListPageFromSearch,
  stripTicketsListPageParam,
} from '@/lib/tickets-list-page-param'
import {
  TICKETS_DEFAULT_SORT_BY,
  TICKETS_DEFAULT_SORT_ORDER,
  cycleTicketsSortSearchParams,
  parseTicketsSortOrder,
  type TicketsSortByKey,
} from '@/lib/tickets-list-sort'
import { useWorkspaceTicketsSortDefaults } from '@/lib/use-workspace-tickets-sort-defaults'
import { buildTagsByProjectIdFromProjects } from '@/lib/project-tags-map'
import { useTicketBarStats } from '@/lib/ticket-bar-stats-context'
import type { BlockTotals, ProjectRow, ProjectTagRow, TicketListItem, WorkspaceUserRow } from '@/types/api'
import { ticketsUrlHasSubpageStyleFilters } from '@/lib/tickets-list-filters-url'
import { IconBookmark } from '@/components/icons'

export function BookmarksPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const { listRevision, clearAll, clearMany } = useBookmarks()
  const openTicketFromList = useOpenTicketFromList()
  const workspaceSortDefaults = useWorkspaceTicketsSortDefaults()

  const pageFromUrl = useMemo(
    () => parseTicketsListPageFromSearch(location.search),
    [location.search],
  )

  const effectiveSortBy =
    searchParams.get('sort_by') ?? workspaceSortDefaults.sortBy ?? TICKETS_DEFAULT_SORT_BY
  const effectiveSortOrder = parseTicketsSortOrder(
    searchParams.get('sort_order') ?? workspaceSortDefaults.sortOrder ?? TICKETS_DEFAULT_SORT_ORDER,
  )

  const onTicketsSort = useCallback(
    (column: TicketsSortByKey) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          stripTicketsListPageParam(p)
          cycleTicketsSortSearchParams(p, column, {
            defaultSortBy: workspaceSortDefaults.sortBy,
            defaultSortOrderWhenMissing: workspaceSortDefaults.sortOrder,
          })
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams, workspaceSortDefaults.sortBy, workspaceSortDefaults.sortOrder],
  )

  const queryKey = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  )

  const pageSize = useMemo(
    () => parseTicketsPageSize(new URLSearchParams(location.search)),
    [location.search],
  )

  const onTicketsPageSizeChange = useCallback(
    (size: TicketsPageSizeOption) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          stripTicketsListPageParam(p)
          if (size === TICKETS_PAGE_SIZE_DEFAULT) p.delete('page_size')
          else p.set('page_size', String(size))
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const onTicketsPageChange = useCallback(
    (nextPage: number) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (nextPage <= 1) p.delete(TICKETS_LIST_PAGE_QUERY)
          else p.set(TICKETS_LIST_PAGE_QUERY, String(nextPage))
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const [rows, setRows] = useState<TicketListItem[]>([])
  const [total, setTotal] = useState(0)
  const [totalExact, setTotalExact] = useState(true)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [blockTotals, setBlockTotals] = useState<BlockTotals | null>(null)
  const [clearing, setClearing] = useState(false)
  const [deletingSelected, setDeletingSelected] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())

  const [usersById, setUsersById] = useState<Map<string, WorkspaceUserRow>>(() => new Map())
  const [projectsById, setProjectsById] = useState<Map<string, ProjectRow>>(() => new Map())
  const [tagsByProjectId, setTagsByProjectId] = useState<Map<string, ProjectTagRow[]>>(() => new Map())

  const { visibility: columnVisibility, setColumnVisible } = useTicketsListColumnVisibility()
  const { setTicketBarStats } = useTicketBarStats()

  const refreshTicketBarCounts = useCallback(
    async (soft?: boolean) => {
      if (!soft) setTicketBarStats(null, true)
      try {
        const r = await api.ticketCounts()
        if (r.workspace) {
          setTicketBarStats(
            {
              total: Number(r.workspace.total_tickets ?? 0) || 0,
              requireProcessing: Number(r.workspace.require_processing ?? 0) || 0,
            },
            false,
          )
        } else {
          setTicketBarStats(null, false)
        }
      } catch {
        setTicketBarStats(null, false)
      }
    },
    [setTicketBarStats],
  )

  useEffect(() => {
    void refreshTicketBarCounts(false)
  }, [refreshTicketBarCounts])

  useEffect(() => {
    return () => setTicketBarStats(null, false)
  }, [setTicketBarStats])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const [ul, pl] = await Promise.all([
          api.workspaceUsers().catch(() => [] as WorkspaceUserRow[]),
          api.projects().catch(() => [] as ProjectRow[]),
        ])
        if (!alive) return
        setUsersById(new Map(ul.map((u) => [u.id, u])))
        setProjectsById(new Map(pl.map((p) => [p.id, p])))
        setTagsByProjectId(buildTagsByProjectIdFromProjects(pl))
      } catch {
        /* ignore */
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const ticketsLoadSeq = useRef(0)

  const load = useCallback(
    async (p: number) => {
      const seq = ++ticketsLoadSeq.current
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams(location.search)
        const base = ticketsParamsFromSearch(params, { aiRoute: false, sortDefaults: workspaceSortDefaults })
        const ps = parseTicketsPageSize(params)
        const metaPromise = api.tickets({
          ...base,
          bookmarked_only: true,
          exclude_resolved_closed: false,
          exclude_emergency: false,
          page: 1,
          page_size: 1,
          include_counts: false,
          counts_only: true,
        })
        const res = await api.tickets({
          ...base,
          bookmarked_only: true,
          exclude_resolved_closed: false,
          exclude_emergency: false,
          page: p,
          page_size: ps,
          include_counts: false,
        })
        if (seq !== ticketsLoadSeq.current) return
        const list = Array.isArray(res.tickets) ? res.tickets : []
        setRows(list)
        if (res.total_exact !== false) {
          setTotal(typeof res.total === 'number' && Number.isFinite(res.total) ? res.total : 0)
          setTotalExact(true)
          setBlockTotals(res.block_totals ?? null)
        } else if (!totalExact) {
          setTotal(typeof res.total === 'number' && Number.isFinite(res.total) ? res.total : 0)
          setBlockTotals(null)
        }
        setPage(typeof res.page === 'number' && res.page >= 1 ? res.page : p)
        void metaPromise
          .then((meta) => {
            if (seq !== ticketsLoadSeq.current) return
            setTotal(typeof meta.total === 'number' && Number.isFinite(meta.total) ? meta.total : 0)
            setTotalExact(meta.total_exact !== false)
            setBlockTotals(meta.block_totals ?? null)
          })
          .catch(() => {
            if (seq !== ticketsLoadSeq.current) return
            if (res.total_exact === false) setTotalExact(false)
          })
      } catch (e) {
        if (seq !== ticketsLoadSeq.current) return
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить закладки')
        setRows([])
        setTotal(0)
        setTotalExact(true)
        setBlockTotals(null)
      } finally {
        if (seq === ticketsLoadSeq.current) setLoading(false)
      }
    },
    [location.search, workspaceSortDefaults, totalExact],
  )

  useEffect(() => {
    void load(pageFromUrl)
  }, [queryKey, pageFromUrl, load, listRevision])

  usePersistTicketListScrollPosition()
  useRestoreTicketsListScrollWhenReady(location.pathname, location.search, !loading)

  const safeTotal = typeof total === 'number' && Number.isFinite(total) ? total : 0
  const safeRows = Array.isArray(rows) ? rows : []
  const pages = Math.max(1, Math.ceil(safeTotal / pageSize))
  const safeProjectFilter = useMemo(
    () => searchParams.get('project_id')?.trim() || searchParams.get('project')?.trim() || '',
    [searchParams],
  )
  const safeSearchFilter = useMemo(
    () => searchParams.get('search')?.trim() || '',
    [searchParams],
  )

  const handleClearAll = useCallback(() => {
    if (clearing || safeTotal <= 0) return
    const target = `${safeProjectFilter ? `проектах` : 'во всех проектах'}${safeSearchFilter ? ` и запросе «${safeSearchFilter}»` : ''}`
    openConfirmModal({
      title: 'Удалить все закладки? ',
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      children: `Это удалит все закладки ${target}. Действие нельзя отменить.`,
      onConfirm: () => {
        setClearing(true)
        const projectIds = safeProjectFilter
          ? safeProjectFilter
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean)
          : undefined
        void clearAll(projectIds, safeSearchFilter || undefined)
          .then(() => {
            void load(1)
            void refreshTicketBarCounts(true)
          })
          .catch((e) => {
            showNotification({
              title: 'Ошибка',
              message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
              color: 'red',
            })
          })
          .finally(() => {
            setClearing(false)
          })
      },
    })
  }, [clearAll, clearing, load, refreshTicketBarCounts, safeProjectFilter, safeSearchFilter, safeTotal])

  const selectedIdsCount = selectedIds.size

  const clearSelectedIds = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const toggleRowSelect = useCallback((ticketId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(ticketId)
      else next.delete(ticketId)
      return next
    })
  }, [])

  const toggleStripSelect = useCallback((rowIds: string[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (!checked) {
        for (const id of rowIds) next.delete(id)
        return next
      }

      for (const id of rowIds) next.add(id)
      return next
    })
  }, [])

  const bulkSelectBase = useMemo(
    (): Omit<TicketsListBulkSelectConfig, 'stripKey'> | null => ({
      selectedIds,
      onRowToggle: toggleRowSelect,
      onStripToggle: toggleStripSelect,
    }),
    [selectedIds, toggleRowSelect, toggleStripSelect],
  )

  const withStripKey = useCallback(
    (stripKey: string): TicketsListBulkSelectConfig | null =>
      bulkSelectBase ? { ...bulkSelectBase, stripKey } : null,
    [bulkSelectBase],
  )

  const handleClearSelected = useCallback(() => {
    if (deletingSelected || selectedIdsCount <= 0) return
    openConfirmModal({
      title: 'Удалить выбранные закладки? ',
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      children: `Будет удалено ${selectedIdsCount} выбранных закладок. Действие нельзя отменить.`,
      onConfirm: () => {
        setDeletingSelected(true)
        void clearMany(Array.from(selectedIds))
          .then(() => {
            clearSelectedIds()
            void load(page)
            void refreshTicketBarCounts(true)
          })
          .catch((e) => {
            showNotification({
              title: 'Ошибка',
              message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
              color: 'red',
            })
          })
          .finally(() => {
            setDeletingSelected(false)
          })
      },
    })
  }, [clearMany, clearSelectedIds, deletingSelected, load, page, refreshTicketBarCounts, selectedIds, selectedIdsCount])

  const grouped = useMemo(
    () =>
      groupTicketsForDisplay(safeRows, user?.id, {
        dateSortOrder: effectiveSortOrder === 'desc' ? 'desc' : 'asc',
        resortWithinGroupsByUpdated: effectiveSortBy === 'updated_at',
      }),
    [safeRows, user?.id, effectiveSortBy, effectiveSortOrder],
  )

  useEffect(() => {
    if (safeRows.length === 0) {
      setSelectedIds(new Set())
      return
    }

    const rowIds = new Set(safeRows.map((row) => row.id))
    setSelectedIds((prev) => {
      const next = new Set<string>()
      for (const id of prev) {
        if (rowIds.has(id)) next.add(id)
      }
      return next
    })
  }, [safeRows])

  const meName = user ? displayNameUser(user) : 'Оператор'

  const countMe =
    blockTotals != null && typeof blockTotals.assigned_to_me === 'number' && Number.isFinite(blockTotals.assigned_to_me)
      ? blockTotals.assigned_to_me
      : grouped.assigned_to_me.length
  const countUn =
    blockTotals != null && typeof blockTotals.unassigned === 'number' && Number.isFinite(blockTotals.unassigned)
      ? blockTotals.unassigned
      : grouped.unassigned.length
  const countOthersTotal = useMemo(() => {
    if (blockTotals?.assigned_to_others && Object.keys(blockTotals.assigned_to_others).length > 0) {
      return Object.values(blockTotals.assigned_to_others).reduce((a, b) => a + (Number(b) || 0), 0)
    }
    return Object.values(grouped.assigned_to_others).reduce((s, arr) => s + arr.length, 0)
  }, [blockTotals, grouped.assigned_to_others])

  const emptyDescription = useMemo(() => {
    const sp = new URLSearchParams(location.search)
    const narrowed =
      ticketsUrlHasSubpageStyleFilters(sp) ||
      Boolean(sp.get('search')?.trim()) ||
      sp.get('assigned') === 'true' ||
      sp.get('assigned') === 'false' ||
      sp.get('assigned_to_me') === 'true' ||
      Boolean(sp.get('assigned_to_id')?.trim()) ||
      Boolean(sp.get('tag_ids')?.trim()) ||
      Boolean(sp.get('created_within_hours')) ||
      (Boolean(sp.get('created_date_from')) && Boolean(sp.get('created_date_to'))) ||
      Boolean(sp.get('updated_within_hours'))
    if (narrowed) return 'По текущим фильтрам список пуст.'
    return 'Добавьте обращения в закладки — иконка слева от темы в списке «Все обращения».'
  }, [location.search])

  const renderGroupedTables = () => {
    if (safeRows.length === 0) return null

    const blocks: ReactNode[] = []

    if (grouped.assigned_to_me.length > 0) {
      blocks.push(
        <TicketsStripBlock key="me" title={`Тикеты ${meName}`} count={countMe}>
          <TicketsListTable
            rows={grouped.assigned_to_me}
            usersById={usersById}
            projectsById={projectsById}
            tagsByProjectId={tagsByProjectId}
            sortBy={effectiveSortBy}
            sortOrder={effectiveSortOrder}
            onSort={onTicketsSort}
            columnVisibility={columnVisibility}
            bulkSelect={withStripKey('me')}
            onRowClick={openTicketFromList}
          />
        </TicketsStripBlock>,
      )
    }

    if (grouped.unassigned.length > 0) {
      blocks.push(
        <TicketsStripBlock key="un" title="Не назначенные" count={countUn}>
          <TicketsListTable
            rows={grouped.unassigned}
            usersById={usersById}
            projectsById={projectsById}
            tagsByProjectId={tagsByProjectId}
            sortBy={effectiveSortBy}
            sortOrder={effectiveSortOrder}
            onSort={onTicketsSort}
            columnVisibility={columnVisibility}
            bulkSelect={withStripKey('un')}
            onRowClick={openTicketFromList}
          />
        </TicketsStripBlock>,
      )
    }

    const otherKeys = Object.keys(grouped.assigned_to_others)
    if (otherKeys.length > 0) {
      blocks.push(
        <Box key="others" className="tickets-strip-block tickets-strip-block--multi">
          <TicketsStripHead title="Назначенные на других" count={countOthersTotal} />
          {otherKeys.map((operatorId) => {
            const opTickets = grouped.assigned_to_others[operatorId]
            if (!opTickets.length) return null
            const op = usersById.get(operatorId)
            const opTitle = op ? displayNameUser(op) : 'Неизвестный оператор'
            const rawOp = blockTotals?.assigned_to_others?.[operatorId]
            const opCount =
              typeof rawOp === 'number' && Number.isFinite(rawOp) ? rawOp : opTickets.length
            return (
              <Fragment key={operatorId}>
                <TicketsStripHead title={opTitle} count={opCount} sub />
                <DataTableScroll className="tickets-table-scroll tickets-strip-table">
                  <TicketsListTable
                    rows={opTickets}
                    usersById={usersById}
                    projectsById={projectsById}
                    tagsByProjectId={tagsByProjectId}
                    sortBy={effectiveSortBy}
                    sortOrder={effectiveSortOrder}
                    onSort={onTicketsSort}
                    columnVisibility={columnVisibility}
                    bulkSelect={withStripKey(`op-${operatorId}`)}
                    onRowClick={openTicketFromList}
                  />
                </DataTableScroll>
              </Fragment>
            )
          })}
        </Box>,
      )
    }

    return (
      <Stack gap={0} className="tickets-page-tickets-stack">
        {blocks}
      </Stack>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Group gap="sm" wrap="wrap">
              <Box>
                <Group gap="xs" wrap="nowrap">
                  <IconBookmark size={20} stroke={1.6} />
                  <Text fw={700} size="lg">
                    Закладки
                  </Text>
                </Group>
                <Text size="sm" c="dimmed">
                  Быстрый доступ к сохранённым обращениям с теми же фильтрами и сортировкой, что в общем списке.
                </Text>
              </Box>
              <Badge variant="light" color="gray">
                {totalExact ? safeTotal : `${safeTotal}+`}
              </Badge>
            </Group>
          }
        />
        <Box
          p="sm"
          style={{
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 8,
            background: 'var(--mantine-color-body)',
          }}
        >
          <PageToolbar
            leading={
              <TicketsListToolbar
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                disabled={loading || deletingSelected}
                onRefresh={() => {
                  void load(page)
                  void refreshTicketBarCounts(true)
                }}
                columnVisibility={columnVisibility}
                onColumnVisibleChange={setColumnVisible}
                workspaceUsers={Array.from(usersById.values())}
                projects={Array.from(projectsById.values())}
                showProjectFilter
                pageSize={pageSize}
                onPageSizeChange={onTicketsPageSizeChange}
                totalCount={totalExact ? safeTotal : undefined}
                bulkActions={
                  selectedIdsCount > 0
                    ? {
                        count: selectedIdsCount,
                        onDeleteSelected: handleClearSelected,
                        onClear: clearSelectedIds,
                        onDeleteSelectedLabel: 'Удалить выбранные',
                      }
                    : null
                }
              />
            }
            trailing={
              <Button
                size="xs"
                color="red"
                variant="light"
                loading={clearing}
                disabled={loading || clearing || safeTotal <= 0}
                onClick={handleClearAll}
              >
                Удалить все закладки
              </Button>
            }
          />
        </Box>

        {error ? (
          <Alert color="red" variant="light" title="Ошибка загрузки">
            {error}
          </Alert>
        ) : null}

        {loading && safeRows.length === 0 ? (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 240,
            }}
          >
            <PageCenterLoader label="Загрузка закладок" minHeight={160} />
          </Box>
        ) : !loading && safeRows.length === 0 ? (
          <EmptyState
            title="Нет закладок"
            description={emptyDescription}
            icon={<IconBookmark size={28} stroke={1.5} />}
          />
        ) : (
          <Stack gap={4} w="100%">
            {pages > 1 ? (
              <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <PaginationBar
                  page={page}
                  pageCount={pages}
                  total={safeTotal}
                  disabled={loading}
                  onPageChange={onTicketsPageChange}
                />
              </Box>
            ) : null}
            {renderGroupedTables()}
            {pages > 1 ? (
              <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <PaginationBar
                  page={page}
                  pageCount={pages}
                  total={safeTotal}
                  disabled={loading}
                  onPageChange={onTicketsPageChange}
                />
              </Box>
            ) : null}
          </Stack>
        )}
      </Stack>
    </PageShell>
  )
}
